/* eslint-disable camelcase */
import React, { useState, useEffect } from 'react';
import {
  View,
  Stack,
  OverlayView,
  Text,
  withTranslation,
  Layout,
  Select,
  Button,
  Badge,
  Skeleton,
  HelpLink,
  StyledContainer,
  Tooltip,
  Trans,
  Link,
  UnorderedList,
  AnimatedCounter,
  Icon,
  ContactForm,
  formatDate,
  formatNumber,
  formatCurrency,
} from '@piwikpro/ui-components';
import moment from 'moment';
import { connect, ConnectedProps } from 'react-redux';
import { ProductAnalytics } from '@piwikpro/product-analytics';
import classNameBind from 'classnames/bind';
import { TFunction } from 'react-i18next';
import { withInject, ConfigService, errorBoundary } from '@piwikpro/platform';
import { Confirmation } from '@piwikpro/confirmation-crate';
import { Organization, OrganizationState } from '@piwikpro/organization-crate';
import { SessionState } from '@piwikpro/auth-crate/reducers/session';
import { InlineError } from '@piwikpro/platform/components/Errors';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { TrafficMonitor } from '@piwikpro/traffic-monitor-crate';
import { RouterService } from '@piwikpro/router-crate';
import { TrafficMonitorState } from '@piwikpro/traffic-monitor-crate/types';
import { Notifications } from '@piwikpro/notification-crate';
import { BillingService } from '../../BillingService';
import { MarketingCratePaymentsState } from '../../redux';
import styles from './ChangePlanOverlay.module.css';
import FeatureList from './components/FeatureList';
import { PlanCard } from './components/PlanCard';
import ChangeProcessingInfoModal, { ChangeProcessingModalVariant } from '../confirmation/ChangeProcessingInfoModal';
import { useIsUnitedStatesCitizen } from './useIsUnitedStatesCitizen';
import CheckoutIframeOverlay from '../CheckoutIframeOverlay';
import {
  type BillingCycle,
  type Tier,
  catalogMaxTrafficLimit,
  findNearestHigherTier,
  findSmallestTrafficLimitAboveUsage,
  findTierByLimits,
  getAction,
  tierSelectorMatchesSubscribed,
  tierSelectorSyncedToSubscriptionForEnterpriseSMB,
  trafficSuggestsEnterpriseBeyondSMB,
} from './subscriptionDomain';

const PRODUCT_ANALYTICS_ACTION_TYPES = {
  offerShown: '@platform/command/payments/offerShown',
  planSelected: '@platform/command/payments/planSelected',
  firsTransactionResult: '@platform/command/payments/firsTransactionResult',
  changeTransactionResult: '@platform/command/payments/changeTransactionResult',
  checkoutOpened: '@platform/command/payments/checkoutOpened',
};

const EMPTY_STATE_TIER = {
  traffic_limit: 25000,
  domains_limit: 1,
  billing_cycle: 'year' as BillingCycle,
};
const EURO_SIGN = '€';
const ENTERPRISE_PLAN_PRICE = 4392;

const bindClasses = classNameBind.bind(styles);

const mapStateToProps = (
  state: {
    session: SessionState
    organization: OrganizationState
    trafficMonitor: TrafficMonitorState
  } & MarketingCratePaymentsState,
) => ({
  isLoading: state.payments.tiers.isLoading
    || state.payments.subscription.isLoading
    || state.organization.productAnalytics.isLoading
    || state.trafficMonitor?.usage.isLoading,
  failed: state.payments.tiers.failed
    || state.organization.productAnalytics.failed,
  userLanguage: state.session.language,
  userId: state.session.userId,
  tiers: state.payments.tiers.details || [],
  currentTierId: state.payments.subscription.details?.price_id,
  currentTierTrafficLimit: state.payments.subscription.details?.traffic_limit,
  currentTierDomainsLimit: state.payments.subscription.details?.domains_limit,
  currentTierBillingCycle: state.payments.subscription.details?.billing_cycle,
  currentSubscriptionPrice: state.payments.subscription.details?.price,
  isPastDue: state.payments.subscription.details?.status === 'past_due',
  hasScheduledDowngrade: state.payments.subscription.details?.scheduled_change_action === 'update',
  hasScheduledCancel: state.payments.subscription.details?.scheduled_change_action === 'cancel',
  blockedDueToPriceAdjustment: state.payments.subscription.details?.blocked_due_to_price_adjustment ?? false,
  organizationId: state.organization.productAnalytics.dataLayer?.organizationId,
  // dataLayer is not most reliable source but we don't have better currently
  organizationName: state.organization.productAnalytics.dataLayer?.organization,
  lastTwoMonthsTrafficUsage: state.trafficMonitor?.usage?.usage,
});

const connector = connect(mapStateToProps);

type ReduxProps = ConnectedProps<typeof connector>;

interface InjectedProps {
  billingService: BillingService
  routerService: RouterService
  organizationService: Organization
  notifications: Notifications
  productAnalytics: ProductAnalytics
  config: ConfigService
  confirmation: Confirmation
  trafficMonitor: TrafficMonitor
}

interface Props extends ReduxProps, InjectedProps {
  t: TFunction
}

const USLegalNote = ({ t }: { t: TFunction }) => (
  <Stack vertical spacing="none">
    <Stack.Item>
      <Text as="p">{t('marketing.change-plan-overlay.us-legal-note.description')}</Text>
    </Stack.Item>
    <Stack.Item>
      <UnorderedList>
        <UnorderedList.Item>
          <Text as="p">{t('marketing.change-plan-overlay.us-legal-note.description-1')}</Text>
        </UnorderedList.Item>
        <UnorderedList.Item>
          <Text as="p">{t('marketing.change-plan-overlay.us-legal-note.description-2')}</Text>
        </UnorderedList.Item>
        <UnorderedList.Item>
          <Text as="p">{t('marketing.change-plan-overlay.us-legal-note.description-3')}</Text>
        </UnorderedList.Item>
        <UnorderedList.Item>
          <Text as="p">
            <Trans
              i18nKey="marketing.change-plan-overlay.us-legal-note.description-4"
              components={[
                <b>text</b>,
              ]}
            />
          </Text>
        </UnorderedList.Item>
      </UnorderedList>
    </Stack.Item>
  </Stack>
);

const ChangePlanOverlay: React.FunctionComponent<Props> = ({
  userLanguage, routerService, t, billingService, trafficMonitor,
  tiers, currentTierId, organizationId, organizationName, isLoading,
  currentTierTrafficLimit, currentTierDomainsLimit, currentTierBillingCycle,
  currentSubscriptionPrice, failed, organizationService, userId, hasScheduledDowngrade, blockedDueToPriceAdjustment, hasScheduledCancel,
  notifications, productAnalytics, config, isPastDue, confirmation, lastTwoMonthsTrafficUsage,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentOrigin = document.location.origin;
  let baseCheckoutOrigin = 'https://success.piwik.pro';

  if (config.get('PADDLE_ENVIRONMENT') === 'sandbox') {
    baseCheckoutOrigin = currentOrigin;
  }

  // Multi-product: the subscribed price_id belongs to the purchased product's catalog and may not
  // exist in the tiers shown here, so match the current tier by its traffic/domains limits instead.
  const currentTier = currentTierTrafficLimit !== undefined
    && currentTierDomainsLimit !== undefined
    && currentTierBillingCycle !== undefined
    ? findTierByLimits(
      tiers,
      currentTierTrafficLimit,
      currentTierDomainsLimit,
      currentTierBillingCycle,
    )
    : undefined;
  const previousTrafficUsage = lastTwoMonthsTrafficUsage?.[0]?.total_traffic || 0;
  const currentTrafficUsage = lastTwoMonthsTrafficUsage?.[1]?.total_traffic || 0;
  const trafficToCompare = previousTrafficUsage > currentTrafficUsage ? previousTrafficUsage : currentTrafficUsage;
  const smbPeakTrafficFromCatalog = catalogMaxTrafficLimit(tiers);
  const smbPlanControlsLockedBeyondCatalogTraffic = smbPeakTrafficFromCatalog > 0
    && trafficToCompare >= smbPeakTrafficFromCatalog;

  const initialTier = findNearestHigherTier(
    tiers, trafficToCompare, currentTier?.billing_cycle || EMPTY_STATE_TIER.billing_cycle, currentTier?.domains_limit || EMPTY_STATE_TIER.domains_limit, currentTier,
  )
    || currentTier
    || findTierByLimits(tiers, EMPTY_STATE_TIER.traffic_limit, EMPTY_STATE_TIER.domains_limit, EMPTY_STATE_TIER.billing_cycle);

  const subscribedPriceMajor = typeof currentSubscriptionPrice === 'string' && currentSubscriptionPrice !== ''
    ? Number(currentSubscriptionPrice) / 100
    : undefined;

  const [tierSelectorState, setTierSelectorState] = useState<Tier | undefined>(initialTier);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState<ChangeProcessingModalVariant | null>(null);
  const [previousPrice, setPreviousPrice] = useState<number>(
    subscribedPriceMajor ?? (initialTier?.price ? initialTier.price / 100 : 0),
  );
  const [checkoutIframeUrl, setCheckoutIframeUrl] = useState<string | null>(null);
  const dataCenter = config.get('SENTRY_ENVIRONMENT');
  const checkoutBaseUrl = config.get('CHECKOUT_BASE_URL');
  const isUnitedStatesCitizen = useIsUnitedStatesCitizen();
  const suggestedTier = findNearestHigherTier(
    tiers,
    trafficToCompare,
    tierSelectorState?.billing_cycle,
    tierSelectorState?.domains_limit,
  );

  const subscriptionHasHigherTrafficRecommendation = Boolean(
    suggestedTier
    && currentTier
    && suggestedTier.traffic_limit > currentTier.traffic_limit,
  );

  const smbTrafficDisabledCurrentEnterpriseOnly = tierSelectorSyncedToSubscriptionForEnterpriseSMB(
    tiers,
    trafficToCompare,
    tierSelectorState,
    currentTier,
    currentTierId,
  );

  const getCheckoutUrl = (priceId: string): string => {
    const ppTheme = localStorage.getItem('pp_theme') || 'light';
    return `${baseCheckoutOrigin}${checkoutBaseUrl}?price_id=${priceId}&organization_id=${organizationId}&organization_name=${organizationName}&user_id=${userId}&language=${userLanguage}&data_center=${dataCenter}&redirect_origin=${encodeURIComponent(currentOrigin)}&pp_theme=${ppTheme}`;
  };

  const fetchData = async () => {
    try {
      await Promise.all([
        billingService.getTiers(),
        organizationService.fetchProductAnalytics(),
        billingService.getCurrentSubscriptionTier(),
        trafficMonitor.getTrafficUsage({
          from: moment(new Date()).add(-1, 'months').format(
            'YYYY-MM',
          ),
          to: moment(new Date()).format(
            'YYYY-MM',
          ),
        }),
      ]);
      // eslint-disable-next-line no-empty
    } catch { }
  };

  if (!tierSelectorState && tiers.length > 0 && !isLoading) {
    setTierSelectorState(
      initialTier,
    );
    if (typeof subscribedPriceMajor !== 'undefined') {
      setPreviousPrice(subscribedPriceMajor);
    } else if (initialTier?.price) {
      setPreviousPrice(initialTier.price / 100);
    }
  }

  useEffect(() => {
    fetchData();
    productAnalytics.push({ type: PRODUCT_ANALYTICS_ACTION_TYPES.offerShown });
  }, []);

  useEffect(() => {
    if (!tierSelectorState || tiers.length === 0 || isLoading) return;

    if (smbTrafficDisabledCurrentEnterpriseOnly) {
      if (typeof currentTier !== 'undefined' && tierSelectorState.id !== currentTier.id) {
        setTierSelectorState(currentTier);
        if (typeof subscribedPriceMajor !== 'undefined') {
          setPreviousPrice(subscribedPriceMajor);
        } else if (typeof currentTier.price !== 'undefined') {
          setPreviousPrice(currentTier.price / 100);
        }
      }
      return;
    }

    const minTraffic = findSmallestTrafficLimitAboveUsage(
      tiers,
      trafficToCompare,
      tierSelectorState.billing_cycle,
      tierSelectorState.domains_limit,
    );
    if (typeof minTraffic === 'undefined') return;

    const matchesSubscribedTierLimits = Boolean(
      currentTier && currentTierId && tierSelectorMatchesSubscribed(tierSelectorState, currentTier),
    );

    if (tierSelectorState.traffic_limit >= minTraffic || matchesSubscribedTierLimits) return;

    const next = findTierByLimits(tiers, minTraffic, tierSelectorState.domains_limit, tierSelectorState.billing_cycle);
    if (next && next.id !== tierSelectorState.id) {
      setTierSelectorState(next);
      if (next.price) setPreviousPrice(next.price / 100);
    }
  }, [tiers, trafficToCompare, tierSelectorState, currentTierId, currentTier, isLoading]);


  const updatePlan = async (planId) => {
    const action = getAction(
      tierSelectorState, currentTier, currentSubscriptionPrice,
    );
    try {
      setPreviewLoading(true);
      await billingService.previewNewSubscription(planId).then(
        async (response) => {
          let creditsIssued = false;
          const {
            immediate_transaction_price,
            immediate_transaction_tax,
            next_billed_at,
            recurring_transaction_price,
            recurring_transaction_tax,
            currency,
          } = response;

          let immediateTransactionPrice = (
            parseInt(immediate_transaction_price, 10) + parseInt(immediate_transaction_tax, 10)
          ) / 100;

          const recurringTransactionPrice = (
            parseInt(recurring_transaction_price, 10) + parseInt(recurring_transaction_tax, 10)
          ) / 100;

          if (immediateTransactionPrice < 0) {
            creditsIssued = true;
            immediateTransactionPrice = Math.abs(immediateTransactionPrice);
          }

          const formattedImmediateTransactionPrice = formatCurrency(
            immediateTransactionPrice,
            { currency },
          );

          const recurringImmediateTransactionPrice = formatCurrency(
            recurringTransactionPrice,
            { currency },
          );
          const isImmediateAction = action === 'upgrade';

          await billingService.applyNewSubscription({
            onConfirmClick: () => {
              if (isImmediateAction) setShowInfoModal('payment_processing');
            },
            onPaymentFailed: () => {
              setShowInfoModal('payment_failed');
            },
            priceId: planId,
            creditsIssued,
            billingCycle: tierSelectorState?.billing_cycle,
            action,
            immediateTransactionPrice: formattedImmediateTransactionPrice,
            recurringTransactionPrice: recurringImmediateTransactionPrice,
            nextBillingPeriod: formatDate(new Date(next_billed_at), { format: 'LL' }),
          }).then(
            () => {
              productAnalytics.push({ type: `${PRODUCT_ANALYTICS_ACTION_TYPES.changeTransactionResult}/success` });
              setShowInfoModal(isImmediateAction ? 'subscription_updated' : 'change_scheduled');
            },
          ).catch((err) => {
            if (err.name !== 'ConfirmationRejectError') {
              productAnalytics.push({ type: `${PRODUCT_ANALYTICS_ACTION_TYPES.changeTransactionResult}/failed` });
              if (err.name === 'PaymentRequiredError') {
                setShowInfoModal('payment_failed');
              } else {
                setShowInfoModal(null);
              }
            }
          });
        },
      ).catch();
      // eslint-disable-next-line no-empty
    } catch { } finally {
      setPreviewLoading(false);
    }
  };

  const openPaddlesCheckout = () => {
    if (!tierSelectorState) return;
    setPreviewLoading(true);

    productAnalytics.push({ type: PRODUCT_ANALYTICS_ACTION_TYPES.checkoutOpened });

    const checkoutUrl = getCheckoutUrl(tierSelectorState.id);

    setCheckoutIframeUrl(checkoutUrl);
  };

  const handleCheckoutSuccess = () => {
    productAnalytics.push({ type: `${PRODUCT_ANALYTICS_ACTION_TYPES.firsTransactionResult}/success` });
    setShowInfoModal('subscription_created');
    setPreviewLoading(false);
  };

  const handleCheckoutClose = () => {
    setCheckoutIframeUrl(null);
    setPreviewLoading(false);
  };

  const onContinueWithNewTierClick = () => {
    const updatePlanFlow = () => {
      if (!currentTierId) {
        openPaddlesCheckout();

        return null;
      }

      if (!tierSelectorState) {
        return null;
      }

      updatePlan(tierSelectorState.id);

      return null;
    };

    if (isUnitedStatesCitizen) {
      confirmation.confirm({
        title: t('marketing.change-plan-overlay.us-legal-note.title'),
        type: 'customBody',
        applyBtnAppearance: 'primary',
        applyBtnText: t('marketing.change-plan-overlay.core-card.continue-button'),
      }, () => <USLegalNote t={t} />).then(() => {
        updatePlanFlow();
      }).catch(() => { });
    } else {
      updatePlanFlow();
    }
  };

  const onDomainsLimitSelect = (value) => {
    if (!tierSelectorState) return;

    const selectedTier = findTierByLimits(
      tiers,
      tierSelectorState.traffic_limit,
      value,
      tierSelectorState.billing_cycle,
    );

    productAnalytics.push({
      type: PRODUCT_ANALYTICS_ACTION_TYPES.planSelected,
      injectPayload: true,
      payload: {
        paymentSelectedTrafficLimit: selectedTier?.traffic_limit,
        paymentSelectedDomainsLimit: selectedTier?.domains_limit,
        paymentSelectedCycle: selectedTier?.billing_cycle,
      },
    });

    if (tierSelectorState.price) {
      setPreviousPrice(tierSelectorState.price / 100);
    }
    setTierSelectorState(selectedTier);
  };

  const onMonthlyActionsLimitSelect = (value) => {
    if (!tierSelectorState) return;

    const selectedTier = findTierByLimits(
      tiers,
      value,
      tierSelectorState.domains_limit,
      tierSelectorState.billing_cycle,
    );
    productAnalytics.push({
      type: PRODUCT_ANALYTICS_ACTION_TYPES.planSelected,
      injectPayload: true,
      payload: {
        paymentSelectedTrafficLimit: selectedTier?.traffic_limit,
        paymentSelectedDomainsLimit: selectedTier?.domains_limit,
        paymentSelectedCycle: selectedTier?.billing_cycle,
      },
    });

    if (tierSelectorState.price) {
      setPreviousPrice(tierSelectorState.price / 100);
    }
    setTierSelectorState(selectedTier);
  };

  const domainsList = Array.from(new Set(tiers.map(tier => tier.domains_limit))).sort((a, b) => a - b);
  const trafficList = Array.from(new Set(tiers.map(tier => tier.traffic_limit))).sort((a, b) => a - b);

  const onTogglerChange = (period) => {
    if (!tierSelectorState) return;

    const selectedTier = findTierByLimits(
      tiers,
      tierSelectorState.traffic_limit,
      tierSelectorState.domains_limit,
      period,
    );
    productAnalytics.push({
      type: PRODUCT_ANALYTICS_ACTION_TYPES.planSelected,
      injectPayload: true,
      payload: {
        paymentSelectedTrafficLimit: selectedTier?.traffic_limit,
        paymentSelectedDomainsLimit: selectedTier?.domains_limit,
        paymentSelectedCycle: selectedTier?.billing_cycle,
      },
    });

    if (tierSelectorState.price) {
      setPreviousPrice(tierSelectorState.price / 100);
    }
    setTierSelectorState(selectedTier);
  };

  const hasActiveSubscription = Boolean(currentTierId && currentTier);

  const minimumTrafficLimitByUsage = tierSelectorState
    ? findSmallestTrafficLimitAboveUsage(
      tiers,
      trafficToCompare,
      tierSelectorState.billing_cycle,
      tierSelectorState.domains_limit,
    )
    : undefined;

  const trafficLimitsForMonthlyActionsSelect = (() => {
    if (!tierSelectorState) return trafficList;

    if (smbTrafficDisabledCurrentEnterpriseOnly && currentTier) return [currentTier.traffic_limit];

    if (typeof minimumTrafficLimitByUsage === 'undefined') return trafficList;

    const selectable = trafficList.filter(limit => limit >= minimumTrafficLimitByUsage);
    const shouldInjectSubscribedTierBelowMinimum = hasActiveSubscription
      && currentTier
      && currentTier.traffic_limit < minimumTrafficLimitByUsage
      && tierSelectorState.billing_cycle === currentTier.billing_cycle
      && tierSelectorState.domains_limit === currentTier.domains_limit
      && !selectable.includes(currentTier.traffic_limit);

    return shouldInjectSubscribedTierBelowMinimum
      ? [...selectable, currentTier.traffic_limit].sort((a, b) => a - b)
      : selectable;
  })();

  const trafficLimitElements = trafficLimitsForMonthlyActionsSelect.map((traffic) => {
    const isCurrentPlanTrafficOption = hasActiveSubscription
      && !!currentTier
      && traffic === currentTier.traffic_limit
      && tierSelectorState?.billing_cycle === currentTier.billing_cycle
      && tierSelectorState?.domains_limit === currentTier.domains_limit;

    const disableCurrentPlanInTrafficSelect = (
      subscriptionHasHigherTrafficRecommendation || smbTrafficDisabledCurrentEnterpriseOnly);

    const currentPlanOptionProps = isCurrentPlanTrafficOption && disableCurrentPlanInTrafficSelect
      ? {
        disabled: true,
        tooltip: {
          content: t('marketing.change-plan-overlay.core-card.continue-button-catalog-usage-tooltip', { usage: formatNumber(Number(trafficToCompare)) }),
        },
      }
      : {};

    return {
      id: traffic,
      value: traffic,
      ...currentPlanOptionProps,
      name: (() => {
        const showCurrentPlanBadge = !!currentTier
          && currentTier.traffic_limit === traffic
          && currentTier.billing_cycle === tierSelectorState?.billing_cycle
          && currentTier.domains_limit === tierSelectorState?.domains_limit;
        const showSuggestedPlanBadge = !showCurrentPlanBadge
          && traffic === suggestedTier?.traffic_limit
          && (
            currentTier?.traffic_limit === undefined
              ? true
              : suggestedTier.traffic_limit > currentTier.traffic_limit
          );

        return (
          <Stack spacing="narrow" verticalAlignment="top">
            <Stack.Item>
              <div className={bindClasses('number-no-wrap')}>
                {formatNumber(Number(traffic))}
              </div>
            </Stack.Item>
            {showCurrentPlanBadge && (
              <Stack.Item fill>
                <Badge
                  display="block"
                  color="white"
                  text={t('marketing.change-plan-overlay.monthly-action-limit-select.current-plan-badge')}
                  size="small"
                  truncate={false}
                />
              </Stack.Item>
            )}
            {showSuggestedPlanBadge && (
              <Stack.Item fill>
                <Badge
                  display="block"
                  color="light-blue"
                  text={t('marketing.change-plan-overlay.monthly-action-limit-select.suggested-plan-badge')}
                  size="small"
                  truncate={false}
                />
              </Stack.Item>
            )}
          </Stack>
        );
      })(),
    };
  });

  const domainsLimitElements = domainsList.map(domain => ({
    id: domain,
    value: domain,
    name: (
      <Stack spacing="narrow" verticalAlignment="top">
        <Stack.Item>
          {formatNumber(Number(domain))}
        </Stack.Item>
        {currentTier?.domains_limit === domain && currentTier.billing_cycle === tierSelectorState?.billing_cycle && (
          <Stack.Item fill>
            <Badge
              display="block"
              color="white"
              text={t('marketing.change-plan-overlay.monthly-action-limit-select.current-plan-badge')}
              size="small"
              truncate={false}
            />
          </Stack.Item>
        )}
      </Stack>
    ),
  }));

  const wrapWithStatus = (children: React.ReactElement): React.ReactElement => {
    if (failed) {
      return (
        <Stack vertical>
          <Stack.Item />
          <Stack.Item>
            <InlineError />
          </Stack.Item>
        </Stack>
      );
    }
    if (isLoading) {
      return (
        <Stack vertical horizontalAlignment="middle">
          <Stack.Item fill previousSpacing="extraWide">
            <Skeleton roundBorder width="100%" height={40} />
          </Stack.Item>
          <Stack.Item>
            <Skeleton roundBorder width={300} height={22} />
          </Stack.Item>
        </Stack>
      );
    }

    return children;
  };

  const isCurrentPlanSelected = Boolean(currentTier && tierSelectorState?.id === currentTier.id);
  const getDisabledButtonTooltip = () => {
    if (smbPlanControlsLockedBeyondCatalogTraffic) return t('marketing.change-plan-overlay.monthly-action-limit-select.current-plan-disabled-tooltip');
    if (isCurrentPlanSelected) return t('marketing.change-plan-overlay.core-card.continue-button-current-plan-tooltip');
    if (isPastDue) return t('marketing.change-plan-overlay.core-card.continue-button-past-due-tooltip');
    if (hasScheduledCancel) return t('marketing.change-plan-overlay.core-card.continue-button-pending-cancel-tooltip');
    if (blockedDueToPriceAdjustment) return t('marketing.change-plan-overlay.core-card.continue-button-pending-migrate-tooltip');
    if (hasScheduledDowngrade) return t('marketing.change-plan-overlay.core-card.continue-button-pending-change-tooltip');
    return '';
  };

  // Determine if the suggested plan caption should be shown
  const isCurrentTierTrafficLimitUndefined = currentTier?.traffic_limit === undefined;
  const isSuggestedTierHigherThanCurrent = (suggestedTier?.traffic_limit ?? 0) > (currentTier?.traffic_limit ?? 0);
  const isSuggestedTierInTrafficLimitElements = trafficLimitElements.some(el => el.value === suggestedTier?.traffic_limit);

  const showSuggestedPlanCaption = Boolean(suggestedTier)
    && (isCurrentTierTrafficLimitUndefined || isSuggestedTierHigherThanCurrent)
    && isSuggestedTierInTrafficLimitElements;

  const showBusinessPlanCaption = trafficSuggestsEnterpriseBeyondSMB(tiers, trafficToCompare, currentTier);

  // Check if on success page
  const isOnSuccessPage = location.pathname.includes(config.get('PAYMENT_BASE_URL')) && location.pathname.includes('/success');

  return (
    <>
      {isOnSuccessPage && (
        <ChangeProcessingInfoModal variant="subscription_created" onClose={() => navigate({ to: '/' })} />
      )}
      {
        showInfoModal && (
          <ChangeProcessingInfoModal
            variant={showInfoModal}
            onClose={() => {
              fetchData();
              setShowInfoModal(null);
              routerService.back();
            }}
          />
        )
      }
      {checkoutIframeUrl && (
        <CheckoutIframeOverlay
          checkoutUrl={checkoutIframeUrl}
          onClose={handleCheckoutClose}
          onSuccess={handleCheckoutSuccess}
        />
      )}
      <OverlayView
        isOpen
        onClose={() => window.close()}
        title={t('marketing.change-plan-overlay.overlay.title')}
      >
        <View>
          <View.Body>
            <Layout maxWidth="medium">
              <Stack
                vertical
                fullHeight
                verticalAlignment="center"
                horizontalAlignment="middle"
                spacing="extraWide"
              >
                <Stack.Item previousSpacing="default" />
                <Stack.Item />
                <Stack.Item>
                  <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">
                    {t('marketing.change-plan-overlay.plan-details.header')}
                  </Text>
                </Stack.Item>
                <Stack.Item previousSpacing="narrow">
                  {/* FIXME: add something to make it responsive */}
                  <div style={{ maxWidth: '750px' }}>
                    <Text as="p" color="InkLighter" align="center">
                      {t('marketing.change-plan-overlay.plan-details.subheader')}
                    </Text>
                  </div>
                </Stack.Item>
                <Stack.Item previousSpacing="narrow" />
                <div className={bindClasses('pricing-plan-card-container')}>
                  <div className={bindClasses('pricing-plan-card')}>
                    <StyledContainer fullHeight border={['left', 'right', 'top', 'bottom']} backgroundColor="white" padding="extraWide">
                      <div className={bindClasses('pricing-plan-inner-container')}>
                        <Stack vertical>
                          <Stack.Item fill>
                            <PlanCard
                              t={t}
                              onPeriodChange={value => onTogglerChange(value)}
                              period={tierSelectorState?.billing_cycle}
                              header={t('marketing.change-plan-overlay.core-card.title')}
                              priceHeader={
                                tierSelectorState?.price && (
                                  <div className={bindClasses('price-row')}>
                                    <span className={bindClasses('base-price')}>
                                      {EURO_SIGN}
                                      <AnimatedCounter
                                        value={tierSelectorState?.price / 100}
                                        startValue={previousPrice}
                                        duration={0.5}
                                        decimals={0}
                                      />
                                    </span>
                                    <Text as="p" size={12} dataId="Caption" color="InkLighter">{t('marketing.change-plan-overlay.core-card.price-caption')}</Text>
                                  </div>
                                )
                              }
                              subheader={t('marketing.change-plan-overlay.core-card.subtitle')}
                            >
                              {wrapWithStatus(
                                <Stack vertical horizontalAlignment="middle">
                                  <Stack.Item fill>
                                    <Select
                                      label={t('marketing.change-plan-overlay.domains-limit-select.label')}
                                      name="domainsLimit"
                                      disabled={smbPlanControlsLockedBeyondCatalogTraffic}
                                      value={tierSelectorState?.domains_limit}
                                      onChange={
                                        (e: { target: { value: string } }) => {
                                          onDomainsLimitSelect(e.target.value);
                                        }
                                      }
                                      options={[
                                        {
                                          id: '',
                                          name: '',
                                          items: domainsLimitElements || [],
                                        },
                                      ]}
                                    />
                                  </Stack.Item>
                                  <Stack.Item fill>
                                    <Select
                                      label={t('marketing.change-plan-overlay.monthly-action-limit-select.label')}
                                      name="monthlyActionsLimit"
                                      disabled={smbPlanControlsLockedBeyondCatalogTraffic}
                                      value={tierSelectorState?.traffic_limit}
                                      onChange={
                                        (e: { target: { value: string } }) =>
                                          onMonthlyActionsLimitSelect(e.target.value)
                                      }
                                      options={[
                                        {
                                          id: '',
                                          name: '',
                                          items: trafficLimitElements || [],
                                        },
                                      ]}
                                      info={showSuggestedPlanCaption && t('marketing.change-plan-overlay.suggested-plan-caption', {
                                        usage: formatNumber(Number(trafficToCompare)),
                                      })}
                                    />
                                  </Stack.Item>
                                  <Stack.Item fill>
                                    <Tooltip position="bottom" content={getDisabledButtonTooltip()}>
                                      <Button
                                        disabled={
                                          smbPlanControlsLockedBeyondCatalogTraffic
                                          || hasScheduledDowngrade || blockedDueToPriceAdjustment || hasScheduledCancel
                                          || isCurrentPlanSelected || isPastDue
                                        }
                                        pending={previewLoading}
                                        fullWidth
                                        text={t('marketing.change-plan-overlay.core-card.continue-button')}
                                        onClick={() => onContinueWithNewTierClick()}
                                        appearance="primary"
                                      />
                                    </Tooltip>
                                  </Stack.Item>
                                </Stack>,
                              )}
                            </PlanCard>
                          </Stack.Item>
                          <Stack.Item previousSpacing="extraWide">
                            {/* TODO: still valid in new licensing 2.0? */}
                            <FeatureList variant="core" />
                          </Stack.Item>
                        </Stack>
                      </div>
                    </StyledContainer>
                  </div>
                  <div className={bindClasses('pricing-plan-card', { 'pricing-plan-card-recommended': showBusinessPlanCaption })}>
                    <StyledContainer fullHeight padding="none" border={['left', 'right', 'top', 'bottom']}>
                      {
                        showBusinessPlanCaption
                        && (
                          <div className={bindClasses('recommended-caption-header')}>
                            <StyledContainer padding="none" backgroundImage="gradient1">
                              <Stack verticalAlignment="center" spacing="narrow">
                                <Stack.Item>
                                  <Icon name="rocket" color="blue" />
                                </Stack.Item>
                                <Stack.Item>
                                  <Text as="p" size={12} dataId="Caption">
                                    {t('marketing.change-plan-overlay.enterprise-card.recommended-caption', { value: formatNumber(Number(trafficToCompare)) })}
                                  </Text>
                                </Stack.Item>
                              </Stack>
                            </StyledContainer>
                          </div>
                        )
                      }
                      <StyledContainer fullHeight backgroundColor="white" padding="extraWide">
                        <div className={bindClasses('pricing-plan-inner-container')}>
                          <Stack vertical fullHeight>
                            <Stack.Item fill>
                              <PlanCard
                                t={t}
                                badge={(
                                  <Badge
                                    color="light-green"
                                    text={t('marketing.change-plan-overlay.enterprise-badge.title')}
                                    size="medium"
                                    truncate={false}
                                  />
                                )}
                                header={t('marketing.change-plan-overlay.enterprise-card.title')}
                                priceHeader={(
                                  <Stack verticalAlignment="center" spacing="narrow">
                                    <Stack.Item>
                                      <Text as="p" color="InkLighter">
                                        <div className={bindClasses('from-label-text')}>
                                          {t('marketing.change-plan-overlay.enterprise-card.from-label')}
                                        </div>
                                      </Text>
                                    </Stack.Item>
                                    <Stack.Item>
                                      <div className={bindClasses('price-row')}>
                                        <div className={bindClasses('base-price')}>
                                          {EURO_SIGN}
                                          {formatNumber(Number(ENTERPRISE_PLAN_PRICE))}
                                        </div>
                                        <Text as="p" size={12} dataId="Caption" color="InkLighter">{t('marketing.change-plan-overlay.core-card.price-caption-enterprise')}</Text>
                                      </div>
                                    </Stack.Item>
                                  </Stack>
                                )}
                                subheader={(
                                  <Trans
                                    t={t}
                                    i18nKey="marketing.change-plan-overlay.enterprise-card.subtitle"
                                    components={[
                                      <Link href="https://piwik.pro/pricing/#plan-overview">
                                        3 plan tiers
                                      </Link>,
                                    ]}
                                  />
                                )}
                              >
                                <ContactForm
                                  onSubmitSuccess={({ message }) => {
                                    notifications.push({
                                      text: message,
                                      type: 'success',
                                      autoCloseSeconds: 5,
                                    });
                                  }}
                                  onSubmitFail={({ message }) => {
                                    notifications.push({
                                      text: message,
                                      type: 'error',
                                      autoCloseSeconds: 5,
                                    });
                                  }}
                                  type="sales"

                                >
                                  {({ open }) => (
                                    <Button
                                      fullWidth
                                      text={t('marketing.change-plan-overlay.enterprise-card.contact-sales-button')}
                                      onClick={() => open()}
                                      appearance="primary"
                                    />
                                  )}
                                </ContactForm>
                              </PlanCard>
                            </Stack.Item>
                            <Stack.Item previousSpacing="extraWide">
                              <FeatureList variant="enterprise" />
                            </Stack.Item>
                          </Stack>
                        </div>
                      </StyledContainer>
                    </StyledContainer>
                  </div>
                </div>
                <Stack.Item />
                <Stack.Item previousSpacing="narrow">
                  <HelpLink href="https://piwik.pro/pricing/">
                    Plan comparison
                  </HelpLink>
                </Stack.Item>
              </Stack>
            </Layout>
          </View.Body>
        </View>
      </OverlayView>
    </>
  );
};

export default connector(withInject<InjectedProps>({
  billingService: 'MarketingCrate.billingService',
  organizationService: 'OrganizationCrate.organization',
  routerService: 'RouterCrate.router',
  trafficMonitor: 'TrafficMonitorCrate.trafficMonitor',
  productAnalytics: 'productAnalytics',
  config: 'config',
  confirmation: 'ConfirmationCrate.confirmation',
  notifications: 'NotificationCrate.notifications',
})(
  withTranslation(['administration', 'platform'])(errorBoundary(ChangePlanOverlay)),
));
